const BLYN_API_BASE: string =
  (typeof window !== 'undefined' && (window as any).__BLYN_API_URL__)
    || 'https://blyn.staging-api.boostt.ai/blyn-v2';

import { blynFetch } from './blynFetch';

export interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
}

export interface ChatAttachment {
  id: string;
  name: string;
  mimeType?: string;
  /** Present only when the file was really uploaded (backend file reference). */
  fileKey?: string;
}

export interface ChoiceOption {
  id: string;
  label: string;
  sublabel?: string;
}

/** Payload of the `choice_options` SSE event — a structured choice list a
 * skill produced (e.g. audience.list → saved audiences) for the chat to
 * render as an interactive picker. The user's pick is sent back as a plain
 * chat message. */
export interface ChoiceOptionsPayload {
  callId: string;
  skill: string;
  prompt: string;
  fieldName: string;
  options: ChoiceOption[];
}

/** A friendly, color-coded status for one skill the agent ran this turn —
 * derived from the `skill_done` SSE event. `level` drives the colour
 * (success=green, error=red, warning=amber, info=neutral); `title` is a
 * short human label and `detail` the plain-language reason (e.g. "Maximum
 * quota reached — archive unused models to free capacity"). */
export type SkillStatusLevel = 'success' | 'error' | 'warning' | 'info';
export interface SkillStatus {
  callId: string;
  skill: string;
  level: SkillStatusLevel;
  title: string;
  detail?: string;
  /** Set when a create returned a resource still being built by the
   * background worker (status "pending") — its progress outlives the chat
   * turn and is followed via watchJobProgress. */
  pendingJob?: BackgroundJob;
}

/** A resource whose creation runs async in a backend worker and exposes
 * GET /api/v1/{collection}/{id}/progress/stream. */
export type BackgroundJobKind = 'journey' | 'campaign';
export interface BackgroundJob {
  kind: BackgroundJobKind;
  id: string;
}

// Skills that can return a still-building resource, and the id field it's
// reported under.
const BACKGROUND_JOB_SKILLS: Record<string, { kind: BackgroundJobKind; idField: string }> = {
  'journey.create': { kind: 'journey', idField: 'journey_id' },
  'campaign.create': { kind: 'campaign', idField: 'campaign_id' },
};

// Friendly nouns for common skills — fallback humanises the raw skill name.
const SKILL_NOUNS: Record<string, string> = {
  'audience.create': 'Audience',
  'audience.purchase': 'Audience purchase',
  'prediction.model.create': 'Prediction model',
  'prediction.model.archive': 'Prediction model',
  'journey.create': 'Journey',
  'campaign.create': 'Campaign',
  'campaign.list': 'Campaigns',
  'content.email.write': 'Email',
  'content.directmail.write': 'Direct mail',
  'content.digitalad.write': 'Digital ad',
  'persona.create': 'Persona',
};

export function humaniseSkill(skill: string): string {
  if (SKILL_NOUNS[skill]) return SKILL_NOUNS[skill];
  // "prediction.model.sync_status" -> "Prediction model sync status"
  const words = skill.replace(/[._]/g, ' ').trim();
  return words.charAt(0).toUpperCase() + words.slice(1);
}

/** Turn a raw `skill_done` payload into a friendly, color-coded status.
 * Returns null for skills not worth surfacing (none today, but keeps the
 * door open). */
export function deriveSkillStatus(data: any): SkillStatus | null {
  const skill: string = data?.skill || data?.name || '';
  if (!skill) return null;
  const noun = humaniseSkill(skill);
  const outcome: string = data?.outcome || 'success';
  const d = data?.data || {};
  const errorMessage: string = data?.user_message || data?.errorMessage || '';
  const isCreate = /\.(create|write|purchase)$/.test(skill);

  // Error — red, show the plain reason.
  if (outcome === 'error') {
    return {
      callId: data?.call_id || '',
      skill,
      level: 'error',
      title: `${noun} — couldn't complete`,
      detail: errorMessage || 'Something went wrong. Please try again.',
    };
  }

  // Input required — neutral/info (the agent is asking for more detail).
  if (outcome === 'input_required') {
    return {
      callId: data?.call_id || '',
      skill,
      level: 'info',
      title: `${noun} — needs more info`,
      detail: data?.ask_message || errorMessage || undefined,
    };
  }

  // Success, but a create whose downstream step didn't fully start — warn
  // (e.g. prediction model saved as draft but training didn't start because
  // a quota was reached). training_started/submit_error are set by the
  // backend for exactly this case.
  if (d.training_started === false || d.submit_error) {
    return {
      callId: data?.call_id || '',
      skill,
      level: 'warning',
      title: `${noun} saved, but training didn't start`,
      detail: d.submit_error || d.note || 'Needs attention before it can run.',
    };
  }

  // Success, but not published to the shared backing service — e.g.
  // journey.create succeeds locally but POST /journeys 401s, so it won't
  // show up on the Journeys page yet. `note` is written as an instruction
  // to the model ("tell the user...", "do not say...") rather than
  // end-user copy, so it's deliberately not surfaced verbatim here.
  if (d.service_published === false) {
    return {
      callId: data?.call_id || '',
      skill,
      level: 'warning',
      title: `${noun} created here, but not published yet`,
      detail: "Running in this chat only — it won't appear in the shared view until publishing succeeds.",
    };
  }

  // Accepted, but the background worker is still building it.
  const jobSkill = BACKGROUND_JOB_SKILLS[skill];
  const jobResourceId = jobSkill && (d[jobSkill.idField] ?? data?.[jobSkill.idField]);
  const jobStatus = d.status ?? data?.status;
  if (jobSkill && jobResourceId && jobStatus === 'pending') {
    return {
      callId: data?.call_id || '',
      skill,
      level: 'info',
      title: `Building ${noun.toLowerCase()}`,
      detail: 'Queued — progress will update here.',
      pendingJob: { kind: jobSkill.kind, id: String(jobResourceId) },
    };
  }

  // Plain success — green.
  return {
    callId: data?.call_id || '',
    skill,
    level: 'success',
    title: isCreate ? `${noun} created` : `${noun} — done`,
  };
}

export interface StreamChatOptions {
  userMessage: string;
  conversationId: string;
  userId: string;
  tenantId?: string;
  projectId?: string;
  history: ChatMessage[];
  state?: Record<string, unknown>;
  showThinking?: boolean;
  attachmentIds?: string[];
  /** Rich attachment metadata — the backend reads `attachments` (not
   * `attachment_ids`) to tell the agent a file exists / was uploaded. */
  attachments?: ChatAttachment[];
  kbSourceIds?: string[];
  /** Rich KB metadata — IDs alone are not enough; send the selected sources
   * the same way attachments are sent. */
  kbSources?: { id: string; name: string; type?: string; project_id?: string | null; scope?: string | null }[];
  onChunk: (text: string) => void;
  onThinking?: (text: string) => void;
  onChoiceOptions?: (payload: ChoiceOptionsPayload) => void;
  onConversationId?: (id: string) => void;
  onToolCall?: (event: { phase: string; call_id: string; name?: string; message?: string; duration?: number; resultSummary?: string }) => void;
  /** Friendly, color-coded per-skill status (green/red/amber). Fired once
   * per `skill_done`. */
  onSkillStatus?: (status: SkillStatus) => void;
  /** Fired once per `skill_start` — lets the UI show a step as running
   * before its outcome arrives. */
  onSkillStart?: (event: { callId: string; skill: string; message?: string }) => void;
  onDone: () => void;
  onError: (error: Error) => void;
  signal?: AbortSignal;
}

export function parseSseBlock(block: string): { event: string; data: any } | null {
  const lines = block.split(/\r?\n/);
  let event = 'message';
  const dataLines: string[] = [];

  for (const line of lines) {
    if (line.startsWith('event:')) {
      event = line.slice(6).trim();
    } else if (line.startsWith('data:')) {
      dataLines.push(line.slice(5).trim());
    }
  }

  const rawData = dataLines.join('\n');
  if (!rawData) return null;
  try {
    return { event, data: JSON.parse(rawData) };
  } catch {
    return { event, data: rawData };
  }
}

export async function streamChat(options: StreamChatOptions): Promise<void> {
  const {
    userMessage, conversationId, userId, tenantId = 't_acme',
    projectId,
    history, state = {}, showThinking = true,
    attachmentIds, attachments, kbSourceIds, kbSources,
    onChunk, onConversationId, onDone, onError, signal,
  } = options;
  try {
    const requestBody = {
      user_message: userMessage,
      conversation_id: conversationId,
      user_id: userId,
      tenant_id: tenantId,
      ...(projectId && { project_id: projectId }),
      history,
      state: {
        ...state,
        ...(kbSourceIds?.length && { kb_source_ids: kbSourceIds }),
      },
      show_thinking: showThinking,
      ...(attachmentIds?.length && { attachment_ids: attachmentIds }),
      ...(attachments?.length && {
        attachments: attachments.map((a) => ({
          id: a.id,
          name: a.name,
          ...(a.mimeType && { mime_type: a.mimeType }),
          ...(a.fileKey && { file_key: a.fileKey }),
        })),
      }),
      ...(kbSourceIds?.length && { kb_source_ids: kbSourceIds }),
      ...(kbSources?.length && {
        kb_sources: kbSources.map((s) => ({
          id: s.id,
          name: s.name,
          ...(s.type && { type: s.type }),
          ...(s.project_id != null && { project_id: s.project_id }),
          ...(s.scope && { scope: s.scope }),
        })),
      }),
    };

    const response = await blynFetch(`${BLYN_API_BASE}/api/v1/process-stream`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'text/event-stream',
      },
      body: JSON.stringify(requestBody),
      signal,
    });

    if (!response.ok) {
      throw new Error(`API error ${response.status}: ${response.statusText}`);
    }

    const reader = response.body?.getReader();
    if (!reader) throw new Error('No response body');

    const decoder = new TextDecoder();
    let buffer = '';
    let streamedText = '';
    // Set by a mid-stream 'error' event (e.g. the loop's cycle detector) —
    // these are diagnostic, not necessarily fatal: the backend still sends
    // a real final answer afterward in 'turn_done' (see
    // app/agent/loop.py's cycle branch + app/api/streaming_runner.py,
    // which always emits an authoritative TurnDone even after an error).
    // Throwing immediately here used to skip straight past that answer
    // and show the raw diagnostic string as if it were the reply.
    let pendingErrorMessage: string | null = null;

    // Handles Unified v13 SSE Event Taxonomy (and backwards compatible with legacy stream)
    const handleParsedEvent = (event: string, data: any): boolean => {
      // Legacy start event
      if (event === 'turn_start') {
        if (data.conversation_id && onConversationId) {
          onConversationId(data.conversation_id);
        }
        return false;
      }
      
      // New start heartbeat
      if (event === 'heartbeat') {
        // Heartbeat helps keep connection alive.
        return false;
      }

      if (event === 'thinking' || event === 'reasoning') {
        const chunk = data.text ?? data.chunk ?? '';
        if (chunk) options.onThinking?.(chunk);
        return false;
      }

      if (event === 'error') {
        // Debug/trace events use type:"error" but have a kind starting with "debug_" — ignore them
        if (typeof data.kind === 'string' && data.kind.startsWith('debug_')) return false;
        // Don't abort the stream here — record it and keep reading. If a
        // 'turn_done' still follows (the common case: cycle-detected /
        // degraded-but-recovered turns), that real answer wins below and
        // this is discarded. Only surfaced via onError if the stream ends
        // without ever reaching turn_done (a genuinely fatal case).
        pendingErrorMessage = data.message || data.user_message || 'Something went wrong';
        return false;
      }

      if (event === 'text_delta' || event === 'text') {
        const chunk = data.text ?? data.chunk ?? '';
        if (chunk) {
          streamedText += chunk;
          onChunk(chunk);
        }
        return false;
      }
      
      if (event === 'tool_call') {
        options.onToolCall?.(data);
        // The blyn_chat surface sends outcome/data/errorMessage on the
        // done phase — surface a friendly status from it too.
        if (data?.phase === 'done' && options.onSkillStatus) {
          const status = deriveSkillStatus(data);
          if (status) options.onSkillStatus(status);
        }
        return false;
      }

      // Native process-stream skill lifecycle. `skill_done` carries the
      // structured outcome/data/error the UI turns into a friendly,
      // color-coded status line; `skill_start` marks the step as running.
      if (event === 'skill_done') {
        if (options.onSkillStatus) {
          const status = deriveSkillStatus(data);
          if (status) options.onSkillStatus(status);
        }
        return false;
      }
      if (event === 'skill_start') {
        const skill: string = data?.skill || data?.name || '';
        if (skill) {
          options.onSkillStart?.({
            callId: data?.call_id || '',
            skill,
            ...(data?.message && { message: String(data.message) }),
          });
        }
        return false;
      }

      if (event === 'suggestions' || event === 'verifier') {
        return false;
      }

      // Structured choice list (e.g. saved audiences) — render as an
      // interactive picker. See blyn-v2 docs/blyn/32-sse-events.md.
      if (event === 'choice_options') {
        if (Array.isArray(data?.options) && data.options.length > 0) {
          options.onChoiceOptions?.({
            callId: data.call_id ?? '',
            skill: data.skill ?? '',
            prompt: data.prompt ?? '',
            fieldName: data.field_name ?? '',
            options: data.options
              .filter((o: any) => o && (o.id || o.label))
              .map((o: any) => ({
                id: String(o.id ?? o.label),
                label: String(o.label ?? o.id),
                ...(o.sublabel && { sublabel: String(o.sublabel) }),
              })),
          });
        }
        return false;
      }

      if (event === 'turn_done' || event === 'done') {
        // A real final answer arrived — supersedes any earlier soft error.
        pendingErrorMessage = null;
        const finalResponse = data.response ?? data.text ?? '';
        // `response` isn't always a repeat of the narration streamed via
        // text_delta — it can be a fuller/reworded final answer (e.g. after
        // a skill failure, or a degraded/cycle-detected turn — see the
        // `error` branch above), or, for skill-heavy turns like journey/
        // content creation, the actual generated deliverable (an email/HTML
        // artifact) that was never streamed at all. Skip it only when it's
        // a genuine duplicate of what's already on screen.
        if (finalResponse && !streamedText.includes(finalResponse.trim())) {
          // Only route it to the artifact panel when it actually looks like
          // an HTML document/fragment (matches isHtmlContent's own check in
          // lib/HtmlPreview.tsx) — otherwise it's plain narration/markdown
          // (e.g. "here's what you can do: 1. ... 2. ...") and fencing it
          // as ```html would make isHtmlContent misread it as an artifact
          // and render it in the "Email preview" panel instead of the chat.
          const looksLikeHtmlDocument =
            finalResponse.includes('<!DOCTYPE html>')
            || (finalResponse.includes('<html') && finalResponse.includes('</html>'))
            || /<table[^>]*>[\s\S]*<\/table>/i.test(finalResponse)
            || /<div[^>]*>[\s\S]*<\/div>/i.test(finalResponse);
          const chunk = looksLikeHtmlDocument ? `\`\`\`html\n${finalResponse}\n\`\`\`` : finalResponse;
          onChunk(streamedText ? `\n\n${chunk}` : chunk);
        }
        onDone();
        return true;
      }

      return false;
    };

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });

      const blocks = buffer.split(/\n\n/);
      buffer = blocks.pop() ?? '';

      for (const block of blocks) {
        if (!block.trim()) continue;

        const parsed = parseSseBlock(block);
        if (!parsed) continue;

        const { event, data } = parsed;
        if (handleParsedEvent(event, data)) {
          return;
        }
      }
    }

    if (buffer.trim()) {
      const parsed = parseSseBlock(buffer);
      if (parsed) {
        const { event, data } = parsed;
        if (handleParsedEvent(event, data)) {
          return;
        }
      }
    }

    // Stream ended without ever reaching turn_done/done. If we saw a soft
    // error along the way, that's the real reason nothing final arrived —
    // surface it now instead of silently calling onDone() with no content.
    if (pendingErrorMessage) {
      onError(new Error(pendingErrorMessage));
      return;
    }
    onDone();
  } catch (error) {
    if (error instanceof Error && error.name === 'AbortError') return;
    onError(error instanceof Error ? error : new Error(String(error)));
  }
}
